import { useEffect, useState } from 'react';
import { listThemes, createTheme, updateTheme, deleteTheme } from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';
import { cx } from '../../components/shared/ui/utils';

const emptyForm = {
  id: null,
  name: '',
  slug: '',
  icon: '',
  description: '',
  isActive: true,
  sortOrder: 0,
};

const AdminPackageThemes = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await listThemes();
      setItems(res?.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load themes');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectItem = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      slug: item.slug || '',
      icon: item.icon || '',
      description: item.description || '',
      isActive: item.is_active !== false,
      sortOrder: item.sort_order || 0,
    });
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        icon: form.icon,
        description: form.description,
        is_active: form.isActive,
        sort_order: form.sortOrder,
      };
      if (form.id) await updateTheme(form.id, payload);
      else await createTheme(payload);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!window.confirm('Delete this theme?')) return;
    try {
      await deleteTheme(form.id);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Themes</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setForm(emptyForm)}>
            New theme
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectItem(item)}
              className={cx(
                'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm',
                form.id === item.id ? 'bg-slate-100' : 'hover:bg-slate-50'
              )}
            >
                <span className="font-semibold text-slate-900">{item.name}</span>
                <span className="text-xs text-slate-500">
                  {item.slug} {item.icon ? `• ${item.icon}` : ''}
                </span>
              </button>
            ))}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Theme name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Slug"
            value={form.slug}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Icon class (optional)"
            value={form.icon}
            onChange={(e) => setForm((prev) => ({ ...prev, icon: e.target.value }))}
          />
          <textarea
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            rows={4}
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <input
            type="number"
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Sort order"
            value={form.sortOrder}
            onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value || 0) }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            {form.id && (
              <Button variant="outline" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={handleSave}>Save theme</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackageThemes;
